import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";

const NUDGE_THRESHOLD_DAYS = 7;

function getDaysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function getNudgeText(lastObservationDate: string | null): string {
  if (!lastObservationDate) return "No observations";
  const d = new Date(lastObservationDate);
  if (isNaN(d.getTime())) return "No observations";
  return formatDistanceToNow(d, { addSuffix: true });
}

function shouldShowNudge(lastObservationDate: string | null): boolean {
  if (!lastObservationDate) return true;
  const days = getDaysSince(lastObservationDate);
  if (days === null) return true;
  return days >= NUDGE_THRESHOLD_DAYS;
}

interface ObservationNudgeProps {
  lastObservationDate: string | null;
  testIdSuffix?: string;
  variant?: "icon" | "inline" | "dot";
}

export function ObservationNudge({ lastObservationDate, testIdSuffix, variant = "icon" }: ObservationNudgeProps) {
  const [expanded, setExpanded] = useState(false);

  if (!shouldShowNudge(lastObservationDate)) return null;

  const hasNone = !lastObservationDate;
  const nudgeText = hasNone ? "No observations" : `Last observation ${getNudgeText(lastObservationDate)}`;
  const colorClass = hasNone
    ? "text-red-600 dark:text-red-500"
    : "text-amber-600 dark:text-amber-500";

  if (variant === "dot") {
    return (
      <span
        className={`absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 rounded-full text-white ${hasNone ? "bg-red-500" : "bg-amber-500"}`}
        title={nudgeText}
        data-testid={`nudge-observation-dot${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      >
        <MessageSquare className="w-2.5 h-2.5" />
      </span>
    );
  }

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2" data-testid={`nudge-observation${testIdSuffix ? `-${testIdSuffix}` : ""}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className={`${colorClass} gap-1.5 px-2`}
          data-testid={`button-nudge-observation${testIdSuffix ? `-${testIdSuffix}` : ""}`}
        >
          <MessageSquare className="w-4 h-4 flex-shrink-0" />
          {expanded && (
            <span className="text-xs text-muted-foreground">{nudgeText}</span>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex-shrink-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        className={colorClass}
        data-testid={`button-nudge-observation${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      >
        <MessageSquare className="w-3.5 h-3.5" />
      </Button>
      {expanded && (
        <div className="absolute right-full top-1/2 -translate-y-1/2 mr-1 z-50 whitespace-nowrap rounded-md bg-popover px-2.5 py-1.5 text-[11px] text-muted-foreground shadow-md border">
          {nudgeText}
        </div>
      )}
    </div>
  );
}
